import React, { useEffect, useState } from 'react';
import { schemesV2Api } from '../../services/schemesV2Api';

/**
 * RazorpayWebhookHandler Component
 * 
 * This component handles Razorpay webhook events for Schemes V2.
 * It should be included in the main App component to listen for webhook events.
 * 
 * Note: In a real application, webhooks are typically handled on the backend.
 * This frontend handler is for demonstration purposes and testing.
 */
const RazorpayWebhookHandler = () => {
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    // Set up webhook event listeners
    const handleWebhookEvent = (event) => {
      try {
        const webhookData = event.detail || event.data;
        console.log('Razorpay webhook event received:', webhookData);
        
        // Process the webhook event
        processWebhookEvent(webhookData);
        
        // Store the event for debugging
        setWebhookEvents(prev => [...prev, {
          id: Date.now(),
          event: webhookData.event || 'unknown',
          timestamp: new Date().toISOString(),
          data: webhookData
        }]);
      } catch (error) {
        console.error('Error processing webhook event:', error);
      }
    };

    // Listen for custom webhook events (for testing)
    window.addEventListener('razorpay-webhook', handleWebhookEvent);
    
    // Listen for payment success events
    window.addEventListener('payment-success', handleWebhookEvent);
    
    // Listen for subscription events
    window.addEventListener('subscription-activated', handleWebhookEvent);
    window.addEventListener('subscription-cancelled', handleWebhookEvent);

    setIsListening(true);

    return () => {
      window.removeEventListener('razorpay-webhook', handleWebhookEvent);
      window.removeEventListener('payment-success', handleWebhookEvent);
      window.removeEventListener('subscription-activated', handleWebhookEvent);
      window.removeEventListener('subscription-cancelled', handleWebhookEvent);
      setIsListening(false);
    };
  }, []);

  const processWebhookEvent = async (webhookData) => {
    try {
      const { event, payload } = webhookData;

      switch (event) {
        case 'payment.captured':
          await handlePaymentCaptured(payload);
          break;
        case 'payment.failed':
          await handlePaymentFailed(payload);
          break;
        case 'subscription.activated':
          await handleSubscriptionActivated(payload);
          break;
        case 'subscription.cancelled':
          await handleSubscriptionCancelled(payload);
          break;
        case 'subscription.charged':
          await handleSubscriptionCharged(payload);
          break;
        default:
          console.log('Unhandled webhook event:', event);
      }
    } catch (error) {
      console.error('Error processing webhook event:', error);
    }
  };

  const handlePaymentCaptured = async (payload) => {
    try {
      const { payment, order } = payload;
      
      if (order?.notes?.enrollment_id) {
        // This is a scheme payment
        const verificationData = {
          razorpay_order_id: order.id,
          razorpay_payment_id: payment.id,
          razorpay_signature: payment.signature || 'webhook_verified',
          enrollment_id: order.notes.enrollment_id,
          installment_number: order.notes.installment_number || 1
        };

        // Verify payment with backend
        await schemesV2Api.verifyPayment(verificationData);
        console.log('Payment verified successfully via webhook');
      }
    } catch (error) {
      console.error('Error handling payment captured webhook:', error);
    }
  };

  const handlePaymentFailed = async (payload) => {
    try {
      const { payment, order } = payload;
      console.log('Payment failed:', payment.id, 'for order:', order.id);
      
      // Handle payment failure logic here
      // You might want to notify the user or update the enrollment status
    } catch (error) {
      console.error('Error handling payment failed webhook:', error);
    }
  };

  const handleSubscriptionActivated = async (payload) => {
    try {
      const { subscription } = payload;
      console.log('Subscription activated:', subscription.id);
      
      // Handle subscription activation logic here
    } catch (error) {
      console.error('Error handling subscription activated webhook:', error);
    }
  };

  const handleSubscriptionCancelled = async (payload) => {
    try {
      const { subscription } = payload;
      console.log('Subscription cancelled:', subscription.id);
      
      // Handle subscription cancellation logic here
    } catch (error) {
      console.error('Error handling subscription cancelled webhook:', error);
    }
  };

  const handleSubscriptionCharged = async (payload) => {
    try {
      const { subscription, payment } = payload;
      console.log('Subscription charged:', subscription.id, 'Payment:', payment.id);
      
      // Handle subscription charge logic here
      // This is similar to payment.captured but for subscription payments
      if (subscription?.notes?.enrollment_id) {
        const verificationData = {
          razorpay_subscription_id: subscription.id,
          razorpay_payment_id: payment.id,
          razorpay_signature: payment.signature || 'webhook_verified',
          enrollment_id: subscription.notes.enrollment_id
        };

        // Verify subscription payment with backend
        await schemesV2Api.verifyPayment(verificationData);
        console.log('Subscription payment verified successfully via webhook');
      }
    } catch (error) {
      console.error('Error handling subscription charged webhook:', error);
    }
  };

  // Simulate webhook events for testing (remove in production)
  const simulateWebhookEvent = (eventType, data) => {
    const event = new CustomEvent('razorpay-webhook', {
      detail: {
        event: eventType,
        payload: data,
        timestamp: new Date().toISOString()
      }
    });
    window.dispatchEvent(event);
  };

  // Debug functions (remove in production)
  const clearWebhookEvents = () => {
    setWebhookEvents([]);
  };

  // This component doesn't render anything visible
  // It's just for handling webhook events
  return null;
};

export default RazorpayWebhookHandler;
